import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { WebSocketService } from '../services/websocket.service';
import { FaaReport } from '../data-models/faa-report.model';
import { FaaReportStatus } from '../data-models/faa-report-status.model';
import { CreateFaaReportRequest } from '../request-models/create-faa-report-request.model';
import { UpdateFaaReportRequest } from '../request-models/update-faa-report-request.model';
import { SubmitFaaReportRequest } from '../request-models/submit-faa-report-request.model';
import { FaaReportProgressEvent } from '../response-models/faa-report-progress-event.model';
import { FaaReportCompleteEvent } from '../response-models/faa-report-complete-event.model';
import { FaaReportFailedEvent } from '../response-models/faa-report-failed-event.model';
import { FaaReportMissingCountSummary } from '../response-models/faa-report-missing-count-summary.model';
import { Page } from '../response-models/page.model';
import { FAA_REPORT_TYPE_VALUES, toCreateFaaReportRequest, toUpdateFaaReportRequest } from '../utils/faa-report-utils';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { orgPrefix } from '../utils/org-path-utils';
import { withChanges } from '../utils/model-utils';

/** Query options for the FAA report list. */
export interface FaaReportListOptions {
  page?: number;
  limit?: number;
  status?: FaaReportStatus;
}

/**
 * FAA compliance reports: `/organizations/:orgId/faa-reports[/:id[/generate|/retry|/submit]]` and
 * `/missing-flight-counts` (hub `user-faa-report-controller.ts`). Paged with `page` / `limit`.
 *
 * Holds the last loaded page as signal state and applies real-time progress from the WebSocket.
 * The `Observable` methods emit an `ApiError` on failure; the `Promise` helpers record the error's
 * message in `error` and resolve `null` / `false`.
 *
 * Reference: /agent-os/specs/2026-01-15-faa-reports/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class FaaReportService extends BaseService<FaaReport> implements OnDestroy {
  private webSocketService = inject(WebSocketService);
  private destroy$ = new Subject<void>();

  // Signal-based state
  private reportsSignal = signal<FaaReport[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(10);

  // Track WebSocket subscription state
  private isSubscribed = false;

  // Public computed signals
  readonly reports = computed(() => this.reportsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Report type options
  readonly reportTypes = FAA_REPORT_TYPE_VALUES;

  // Computed stats
  readonly generatingReports = computed(() => this.reportsSignal().filter(r => r.status === 'generating'));
  readonly readyReports = computed(() => this.reportsSignal().filter(r => r.status === 'ready'));
  readonly failedReports = computed(() => this.reportsSignal().filter(r => r.status === 'failed'));

  constructor() {
    super('faa-reports', FaaReport);
    // Subscribe to WebSocket events when connection state changes to authenticated
    this.webSocketService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // API METHODS
  // ============================================

  /** One page of an organization's FAA reports. */
  listReports(orgId: string, options?: FaaReportListOptions): Observable<Page<FaaReport>> {
    return this.getPage({
      prefix: orgPrefix(orgId),
      query: { page: options?.page, limit: options?.limit, status: options?.status },
    });
  }

  /**
   * How many records in a would-be filing have no flight count entered and would be filed as 1 (FAA §F.2.d).
   *
   * Advisory only: resolves `null` on any failure. A warning that cannot be computed must not stop someone
   * filing: an under-count is a compliance problem, being unable to file at all is worse.
   */
  async fetchMissingFlightCounts(orgId: string, startDate: string, endDate: string): Promise<FaaReportMissingCountSummary | null> {
    try {
      const opts = { prefix: orgPrefix(orgId), path: 'missing-flight-counts', query: { startDate, endDate } };
      return await firstValueFrom(this.request('GET', opts, toMissingCountSummary));
    } catch (error: unknown) {
      console.error('Error fetching missing flight counts:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Load a page of reports into signal state. Failures set `error`. */
  async loadReports(orgId: string, options?: FaaReportListOptions): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const page = await firstValueFrom(this.listReports(orgId, options));
      this.reportsSignal.set(page.items);
      this.totalCountSignal.set(page.paging.totalItems);
      this.currentPageSignal.set(page.paging.page);
      this.pageSizeSignal.set(page.paging.pageSize);
    } catch (error: unknown) {
      this.recordError(error, 'Failed to load reports');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** A single report, including its pre-signed `downloadUrl`. */
  getReport(orgId: string, reportId: string): Observable<FaaReport | null> {
    return this.get(reportId, { prefix: orgPrefix(orgId) });
  }

  /** Fetch a single report; resolves `null` on failure. */
  async fetchReport(orgId: string, reportId: string): Promise<FaaReport | null> {
    try {
      return await firstValueFrom(this.getReport(orgId, reportId));
    } catch (error: unknown) {
      console.error('Error fetching FAA report:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Create a new FAA report in draft status. */
  createReport(orgId: string, data: CreateFaaReportRequest): Observable<FaaReport | null> {
    return this.create(toCreateFaaReportRequest(data), { prefix: orgPrefix(orgId) });
  }

  /** Create a report and add it to signal state; resolves `null` on failure. */
  async createAndAddReport(orgId: string, data: CreateFaaReportRequest): Promise<FaaReport | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const newReport = await firstValueFrom(this.createReport(orgId, data));
      if (newReport) {
        this.reportsSignal.update(reports => [newReport, ...reports]);
        this.totalCountSignal.update(count => count + 1);
      }
      return newReport;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to create report');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Update a draft report (`name`, `options`). */
  updateReport(orgId: string, reportId: string, data: UpdateFaaReportRequest): Observable<FaaReport | null> {
    return this.update(reportId, toUpdateFaaReportRequest(data), { prefix: orgPrefix(orgId) });
  }

  /** Update a report and replace it in signal state; resolves `null` on failure. */
  async updateAndRefreshReport(orgId: string, reportId: string, data: UpdateFaaReportRequest): Promise<FaaReport | null> {
    try {
      const updatedReport = await firstValueFrom(this.updateReport(orgId, reportId, data));
      if (updatedReport) {
        this.replaceReport(updatedReport);
      }
      return updatedReport;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to update report');
      return null;
    }
  }

  /** Delete (soft) a report. The hub refuses while it is generating. */
  deleteReport(orgId: string, reportId: string): Observable<void> {
    return this.delete(reportId, { prefix: orgPrefix(orgId) });
  }

  /** Delete a report and remove it from signal state; resolves `false` on failure. */
  async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.deleteReport(orgId, reportId));
      this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
      this.totalCountSignal.update(count => Math.max(0, count - 1));
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to delete report');
      return false;
    }
  }

  /** Queue PDF generation for a report. Emits the queued job's id. */
  generatePdf(orgId: string, reportId: string): Observable<{ jobId: string }> {
    return this.request('POST', { prefix: orgPrefix(orgId), id: reportId, path: 'generate' }, toJobResult, {});
  }

  /** Trigger PDF generation and mark the report generating; resolves `false` on failure. */
  async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.generatePdf(orgId, reportId));
      this.patchReport(reportId, { status: 'generating', generationProgress: 0 });
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to start generation');
      return false;
    }
  }

  /** Retry a failed report generation. Emits the queued job's id. */
  retryReport(orgId: string, reportId: string): Observable<{ jobId: string }> {
    return this.request('POST', { prefix: orgPrefix(orgId), id: reportId, path: 'retry' }, toJobResult, {});
  }

  /** Retry a failed report and mark it generating; resolves `false` on failure. */
  async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.retryReport(orgId, reportId));
      this.patchReport(reportId, { status: 'generating', generationProgress: 0, errorMessage: undefined });
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to retry generation');
      return false;
    }
  }

  /** Record a report as submitted to `email` (ADN sends nothing). Emits the updated report. */
  submitReport(orgId: string, reportId: string, email: string): Observable<FaaReport | null> {
    const body: SubmitFaaReportRequest = { email };
    return this.create(body, { prefix: orgPrefix(orgId), id: reportId, path: 'submit' });
  }

  /** Submit a report and replace it in signal state; resolves `false` on failure. */
  async submitAndUpdateReport(orgId: string, reportId: string, email: string): Promise<boolean> {
    try {
      const updatedReport = await firstValueFrom(this.submitReport(orgId, reportId, email));
      if (updatedReport) {
        this.replaceReport(updatedReport);
      }
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to submit report');
      return false;
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /** Get report by ID from local state */
  getReportById(reportId: string): FaaReport | undefined {
    return this.reportsSignal().find(r => r.id === reportId);
  }

  /** Get human-readable status text */
  getStatusText(status: FaaReportStatus): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating...';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /** Get Bootstrap icon class for status */
  getStatusIcon(status: FaaReportStatus): string {
    switch (status) {
      case 'draft': return 'bi-file-earmark';
      case 'generating': return 'bi-arrow-repeat';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-file-earmark';
    }
  }

  /** Get report type label from value */
  getReportTypeLabel(value: string | null | undefined): string {
    return this.reportTypes.find(t => t.value === value)?.label || value || '';
  }

  /** Format date range for display */
  formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const format = (date: string | null | undefined) => (date ? new Date(date).toLocaleDateString('en-US', options) : '—');
    return `${format(startDate)} - ${format(endDate)}`;
  }

  /** Check if report can be deleted */
  canDelete(report: FaaReport): boolean {
    return report.status !== 'generating';
  }

  /** Check if report can be retried */
  canRetry(report: FaaReport): boolean {
    return report.status === 'failed';
  }

  /** Check if report can generate PDF */
  canGeneratePdf(report: FaaReport): boolean {
    return report.status === 'draft';
  }

  /** Check if report can be submitted */
  canSubmit(report: FaaReport): boolean {
    return report.status === 'ready';
  }

  /** Clear error state */
  clearError(): void {
    this.errorSignal.set(null);
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /** Listen for progress, complete and failed events once the socket is authenticated. */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    this.webSocketService.on<FaaReportProgressEvent>('faa-report:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, { status: event.status, generationProgress: event.progress }));

    this.webSocketService.on<FaaReportCompleteEvent>('faa-report:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, {
        status: event.status,
        reportUrl: event.url,
        recordCount: event.recordCount,
        fileSize: event.fileSize,
        generationProgress: 100,
      }));

    this.webSocketService.on<FaaReportFailedEvent>('faa-report:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchReport(event.reportId, { status: event.status, errorMessage: event.error, generationProgress: 0 }));
  }

  /** Replaces a report in state with a copy (same class) carrying `changes`. */
  private patchReport(reportId: string, changes: Partial<FaaReport>): void {
    this.reportsSignal.update(reports =>
      reports.map(report => (report.id === reportId ? withChanges(report, changes) : report))
    );
  }

  private replaceReport(updated: FaaReport): void {
    this.reportsSignal.update(reports => reports.map(r => (r.id === updated.id ? updated : r)));
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[FaaReportService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
  }
}



function toJobResult(data: unknown): { jobId: string } {
  return { jobId: (isRecord(data) ? nullableString(data['jobId']) : undefined) ?? '' };
}

function toMissingCountSummary(data: unknown): FaaReportMissingCountSummary | null {
  return isRecord(data) ? (data as unknown as FaaReportMissingCountSummary) : null;
}
